/**
 * @fileoverview Main application component for the Storyteller
 * @example
 * // Usage in a component:
 * import { App } from './App'
 *
 * function Component() {
 *   return <App assignmentId="123" />
 * }
 */

import { AxiosError } from 'axios'
import { useEffect, useState } from 'react'

import { RTCContext } from '../RTCContext'
import { useQuestion } from '../hooks/useQuestion'
import { useSubmit } from '../hooks/useSubmit'
import { Question } from './Question'

import { DataContext } from '../DataContext'
import { MiddlewareContext } from '../MiddlewareContext'
import { useAssets } from '../hooks/useAssets'
import { useFeatureFlag } from '../hooks/useFeatureFlag'
import { useICEServers } from '../hooks/useICEServers'
import { useSentry } from '../hooks/useSentry'
import { useSessionStorage } from '../hooks/useSessionStorage'
import './App.module.css'
import { GlobalModalContext } from './GlobalModalContext'
import { NoTest } from './NoTest'
import { ProcessingButton } from './base/ProcessingButton'

/**
 * @interface AppProps
 * @description Props for the App component
 * @property {string} assignmentId - The ID of the assignment
 * @property {boolean} standalone - Whether the app is standalone
 * @property {Function} onLoad - Callback function for loading the assignment
 * @property {Function} onProgress - Callback function for progress updates
 * @property {Function} onComplete - Callback function for completing the assignment
 * @property {Function} onLanguageChange - Callback function for language changes
 * @property {Function} onTimeout - Callback function for timeouts
 * @property {Function} onExit - Callback function for exiting the assignment
 */
export interface AppProps {
    assignmentId: string
    standalone?: boolean
    onLoad?: (...args: unknown[]) => Promise<void>
    onProgress?: (...args: unknown[]) => Promise<void>
    onComplete?: () => Promise<void>
    onLanguageChange?: (language: string) => Promise<void>
    onTimeout?: () => Promise<void>
    onExit?: () => Promise<void>
}

/**
 * @function isComplete
 * @description Checks if the status indicates completion
 * @param {number} status - The status code to check
 * @returns {boolean} Whether the status indicates completion
 */
function isComplete(status: number): boolean {
    return status === 423 || status === 204 || status === 417
}

/**
 * @function isRedirect
 * @description Checks if the status indicates a redirect
 * @param {number} status - The status code to check
 * @returns {boolean} Whether the status indicates a redirect
 */
function isRedirect(status: number): boolean {
    return status === 210
}

/**
 * @function isAssignmentExpired
 * @description Checks if the status indicates an expired assignment
 * @param {number} status - The status code to check
 * @returns {boolean} Whether the status indicates an expired assignment
 */
function isAssignmentExpired(status: number): boolean {
    return status === 408
}

/**
 * @function isExit
 * @description Checks if the status indicates an exit
 * @param {number} status - The status code to check
 * @returns {boolean} Whether the status indicates an exit
 */
function isExit(status: number): boolean {
    return status === 406
}

/**
 * @function confirmExit
 * @description Handles the beforeunload event to confirm the exit
 * @param {BeforeUnloadEvent} event - The event object
 */
function confirmExit(event: BeforeUnloadEvent) {
    event.preventDefault()
    event.stopPropagation()
    event.returnValue = ''
}

/**
 * @function App
 * @description Main application component for the Storyteller
 * @param {AppProps} props - The props for the App component
 * @returns {JSX.Element} The App component
 */
export function App({
    assignmentId,
    standalone = false,
    onComplete = () => Promise.resolve(),
    onTimeout = () => Promise.resolve(),
    onLanguageChange = () => Promise.resolve(),
    onExit = () => Promise.resolve(),
    onLoad = () => Promise.resolve(),
    onProgress = () => Promise.resolve(),
}: AppProps) {
    const iceServers = useICEServers()
    const [question, setQuestion] = useState<Novoic.AnyQuestion>()
    const [showStub, setShowStub] = useState<boolean>(false)
    const [isLegacyBattery, setIsLegacyBattery] = useState<boolean>()
    const [isExpired, setIsExpired] = useState<boolean>(false)
    const areAssetsLoaded = useAssets()
    const [errors, setErrors] = useState<Record<string, Record<string, unknown>>>({})
    const legacyBatteries = useFeatureFlag<string[]>('old_fashion_batteries')
    const [onSubmit] = useSubmit()
    // TODO: Rewrite useQuestion to something like useBootstrapData, because it's not only about questions
    const [bootstrapData, status] = useQuestion(assignmentId)
    const batteryId = (bootstrapData as unknown as Record<string, Novoic.Assignment>)?.assignment
        ?.battery_id
    const { set } = useSessionStorage()

    const userAgent = navigator.userAgent
    const isFacebookBrowser = /Android/i.test(userAgent) && /FBAV|FBAN|FB_IAB/i.test(userAgent);

    useEffect(() => useSentry(), [])

    useEffect(() => {
        window.addEventListener('beforeunload', confirmExit)

        return () => window.removeEventListener('beforeunload', confirmExit)
    }, [])

    useEffect(() => {
        if (question) {
            set<Novoic.AnyQuestion>('lastQuestion', question)
        }
    }, [question])

    useEffect(() => {
        if (bootstrapData && legacyBatteries) {
            setIsLegacyBattery(legacyBatteries.includes(batteryId))
        }
    }, [bootstrapData, legacyBatteries])

    useEffect(() => {
        if (isFacebookBrowser) {
            setQuestion({
                type: 'checkbox',
                id: 'unsupported_browser',
                question: {
                    'category': 'unsupported-browser',
                }
            } as unknown as Novoic.AnyQuestion)
            return
        }

        if (bootstrapData) {
            onLoad?.(bootstrapData, status)
        }

        if (isAssignmentExpired(status)) {
            setIsExpired(true)
            setShowStub(true)
        } else if (isRedirect(status)) {
            window.removeEventListener('beforeunload', confirmExit)
            setQuestion({
                type: 'redirect' as Novoic.QuestionType,
                id: 'redirect',
                question: {
                    location: (bootstrapData as unknown as Record<string, string>).location,
                },
            } as Novoic.AnyQuestion)
        } else if (isExit(status)) {
            onExit?.()
            setShowStub(true)
        } else if (isComplete(status)) {
            onComplete?.()
            setShowStub(true)
        } else {
            setQuestion(bootstrapData)
        }
    }, [bootstrapData, status])

    if (showStub) {
        return <NoTest />
    }

    /**
     * If the question is not yet fetched and not expired
     */
    if (!question || !areAssetsLoaded) {
        if (!isExpired) {
            return <ProcessingButton />
        }
    }

    /**
     * Universal submit handler
     * @param {string} questionID ID of the question
     * @param answer Value to submit as answer
     * @returns {Promise<Novoic.ValidationError[] | void>}
     */
    async function submit<T extends Record<string, unknown>>(
        questionID: string,
        answer: T
    ): Promise<Record<string, unknown> | void> {
        try {
            const [newQuestion, status] = await onSubmit({
                answer,
            })

            sessionStorage.clear()

            if (isRedirect(status)) {
                window.removeEventListener('beforeunload', confirmExit)
                setQuestion({
                    type: 'redirect' as Novoic.QuestionType,
                    id: 'redirect',
                    question: {
                        location: newQuestion.location as string,
                    },
                } as Novoic.AnyQuestion)
                return
            }

            if (isComplete(status)) {
                onComplete?.()
                return
            }

            onProgress(newQuestion, status)
            setErrors({ [questionID]: undefined })
            setQuestion(newQuestion as unknown as Novoic.AnyQuestion)
        } catch (e: unknown) {
            const response = (e as AxiosError).response
            if (isAssignmentExpired(response.status)) {
                setIsExpired(true)
                return
            }

            const { data } = response.data as Novoic.SubmitResponse<Record<string, unknown>>

            if (response) {
                setErrors({ [questionID]: data })
            }

            return data
        }
    }

    async function exit(): Promise<void> {
        sessionStorage.clear()
        localStorage.clear()
        onExit?.()
    }

    return (
        <RTCContext.Provider value={{ iceServers }}>
            <DataContext.Provider value={{ assignmentId, standalone, setIsExpired }}>
                <MiddlewareContext.Provider
                    value={{
                        assignmentId,
                        isLegacyBattery,
                        question,
                        onTimeout,
                        onLanguageChange,
                        standalone,
                        isExpired,
                        errors: errors[question?.id],
                        submit,
                        exit,
                    }}
                >
                    <GlobalModalContext>
                        <Question />
                    </GlobalModalContext>
                </MiddlewareContext.Provider>
            </DataContext.Provider>
        </RTCContext.Provider>
    )
}
